// theme.js - Enhanced with comprehensive dark mode support
import { createTheme } from '@mui/material/styles';

export const getTheme = (isDark) => {
  return createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: {
        main: '#1976d2',
        light: '#42a5f5',
        dark: '#1565c0',
      },
      secondary: {
        main: '#ea3232',
        light: '#ff6b6b',
        dark: '#c62828',
      },
      error: {
        main: '#f44336',
      },
      warning: {
        main: '#ff9800',
      },
      info: {
        main: '#2196f3',
      },
      success: {
        main: '#4caf50',
      },
      background: {
        default: isDark ? '#1f262e' : '#f5f7fa',
        paper: isDark ? '#2b323c' : '#ffffff',
      },
      // Add elevation/depth for cards and surfaces
      custom: {
        elevated: isDark ? '#2b323c' : '#ffffff',
        background: isDark ? '#1f262e' : '#f5f7fa',
      },
      text: {
        primary: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
        secondary: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
        disabled: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.38)',
      },
      divider: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
      action: {
        active: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.54)',
        hover: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
        selected: isDark ? 'rgba(255, 255, 255, 0.16)' : 'rgba(0, 0, 0, 0.08)',
        disabled: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
        disabledBackground: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: isDark ? '#1f262e' : '#f5f7fa',
            color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
            // Custom scrollbar styling
            '& *::-webkit-scrollbar': {
              width: '12px',
              height: '12px',
            },
            '& *::-webkit-scrollbar-track': {
              backgroundColor: isDark ? '#1f262e' : '#f5f7fa',
            },
            '& *::-webkit-scrollbar-thumb': {
              backgroundColor: isDark ? '#4a5568' : '#cbd5e0',
              borderRadius: '6px',
              border: `2px solid ${isDark ? '#1f262e' : '#f5f7fa'}`,
              '&:hover': {
                backgroundColor: isDark ? '#5a6578' : '#a0aec0',
              },
            },
            // Firefox scrollbar
            scrollbarWidth: 'thin',
            scrollbarColor: isDark ? '#4a5568 #1f262e' : '#cbd5e0 #f5f7fa',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? '#2b323c' : '#ffffff',
            backgroundImage: 'none',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
            backgroundColor: isDark ? '#2b323c' : '#ffffff',
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#2b323c' : '#ffffff',
            color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#2b323c' : '#ffffff',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#2b323c' : '#ffffff',
            backgroundImage: 'none',
            color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#2b323c' : '#ffffff',
            backgroundImage: 'none',
            color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
            // Add slight elevation effect in dark mode
            boxShadow: isDark 
              ? '0 2px 4px rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.2)' 
              : '0 2px 4px rgba(0, 0, 0, 0.05)',
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
            backgroundColor: isDark ? '#2b323c' : '#ffffff',
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#2b323c' : '#ffffff',
            color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
          },
          title: {
            color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
          },
          subheader: {
            color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
          },
        },
      },
      MuiCardActions: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#2b323c' : '#ffffff',
          },
        },
      },
      MuiBox: {
        styleOverrides: {
          root: {
            // This helps Box components inherit proper colors
            '&[class*="elevated"], &[data-elevated="true"]': {
              backgroundColor: isDark ? '#2b323c' : '#ffffff',
              color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#2b323c' : '#ffffff',
            backgroundImage: 'none',
            color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? '#2b323c' : '#ffffff',
            backgroundImage: 'none',
            color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#2b323c' : '#ffffff',
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
            },
            '&.Mui-selected': {
              backgroundColor: isDark ? 'rgba(25, 118, 210, 0.16)' : 'rgba(25, 118, 210, 0.08)',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(25, 118, 210, 0.24)' : 'rgba(25, 118, 210, 0.12)',
              },
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
            },
            '&.Mui-selected': {
              backgroundColor: isDark ? 'rgba(25, 118, 210, 0.16)' : 'rgba(25, 118, 210, 0.08)',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(25, 118, 210, 0.24)' : 'rgba(25, 118, 210, 0.12)',
              },
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.54)',
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
          },
          secondary: {
            color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#2b323c' : '#ffffff',
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#2b323c' : '#ffffff',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
            borderBottom: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
            backgroundColor: isDark ? '#2b323c' : '#ffffff',
          },
          head: {
            fontWeight: 600,
            backgroundColor: isDark ? '#1f262e' : '#f5f7fa',
            color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
          },
          body: {
            backgroundColor: isDark ? '#2b323c' : '#ffffff',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#2b323c' : '#ffffff',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
            },
            '&.Mui-selected': {
              backgroundColor: isDark ? 'rgba(25, 118, 210, 0.12)' : 'rgba(25, 118, 210, 0.08)',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(25, 118, 210, 0.2)' : 'rgba(25, 118, 210, 0.12)',
              },
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: isDark ? '#1f262e' : '#ffffff',
              color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
              '& fieldset': {
                borderColor: isDark ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
              },
              '&:hover fieldset': {
                borderColor: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1976d2',
              },
            },
            '& .MuiInputLabel-root': {
              color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
            },
            '& .MuiInputBase-input': {
              color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#1f262e' : '#ffffff',
            color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
          },
          icon: {
            color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.54)',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
            },
            '&.Mui-selected': {
              backgroundColor: isDark ? 'rgba(25, 118, 210, 0.16)' : 'rgba(25, 118, 210, 0.08)',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(25, 118, 210, 0.24)' : 'rgba(25, 118, 210, 0.12)',
              },
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? '#2b323c' : '#ffffff',
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? '#2b323c' : '#ffffff',
            color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? '#1f262e' : 'rgba(97, 97, 97, 0.9)',
            color: '#ffffff',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#1f262e' : '#e0e0e0',
            color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.54)',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
            },
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#2b323c' : '#ffffff',
            color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
            '&:before': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
            },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#2b323c' : '#ffffff',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
            },
          },
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#1f262e' : '#f5f7fa',
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
            backgroundColor: isDark ? '#1f262e' : '#ffffff',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#1f262e' : '#ffffff',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#1976d2',
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
            '&.Mui-selected': {
              color: isDark ? '#ffffff' : '#1976d2',
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#2b323c' : '#ffffff',
          },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            // Containers with elevation should use paper color
            '&[class*="elevated"]': {
              backgroundColor: isDark ? '#2b323c' : '#ffffff',
            },
          },
        },
      },
      MuiGrid: {
        styleOverrides: {
          root: {
            // Grid items that are cards/elevated
            '&[data-card="true"], &[class*="card"]': {
              backgroundColor: isDark ? '#2b323c' : '#ffffff',
              borderRadius: 8,
            },
          },
        },
      },
      MuiStack: {
        styleOverrides: {
          root: {
            '&[data-elevated="true"]': {
              backgroundColor: isDark ? '#2b323c' : '#ffffff',
              color: isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
            },
          },
        },
      },
    },
    shape: {
      borderRadius: 8,
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    },
  });
};